/**
 * ProjectionForm Component
 * Form for creating and editing box office projections with real-time validation
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Chip,
  Alert
} from '@mui/material';
import {
  AttachMoney,
  CalendarToday,
  Info,
  TrendingUp,
  TrendingDown,
  HelpOutline
} from '@mui/icons-material';

const ProjectionForm = ({
  formData,
  errors,
  touched,
  onFieldChange,
  hasFieldError,
  getFieldError,
  isEditMode = false,
  disabled = false
}) => {
  const [calculatedValues, setCalculatedValues] = useState({
    projectionFloor: 0,
    projectionCeiling: 0,
    range: 0,
    rangePercentage: 0
  });

  // Calculate derived values when actual projection changes
  useEffect(() => {
    const actual = parseFloat(formData.actualProjection) || 0;
    const floor = actual * 0.9;
    const ceiling = actual * 1.1;
    const range = ceiling - floor;
    const rangePercentage = actual > 0 ? (range / actual) * 100 : 0;

    setCalculatedValues({
      projectionFloor: floor,
      projectionCeiling: ceiling,
      range,
      rangePercentage
    });
  }, [formData.actualProjection]);

  // Handle currency input formatting
  const handleCurrencyChange = (field, value) => {
    // Remove non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    onFieldChange(field, numericValue);
  };

  // Format currency for display
  const formatCurrencyDisplay = (value) => {
    if (!value || isNaN(parseFloat(value))) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseFloat(value));
  };

  // Get confidence level based on range
  const getConfidenceLevel = () => {
    if (calculatedValues.rangePercentage <= 50) return { level: 'High', color: 'success' };
    if (calculatedValues.rangePercentage <= 100) return { level: 'Medium', color: 'warning' };
    return { level: 'Low', color: 'error' };
  };

  const confidence = getConfidenceLevel();

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        {isEditMode ? 'Edit Projection Details' : 'Projection Details'}
      </Typography>

      <Grid container spacing={3}>
        {/* Film ID (Read-only for edit mode) */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Film ID (IMDB)"
            value={formData.filmId}
            onChange={(e) => onFieldChange('filmId', e.target.value)}
            error={hasFieldError('filmId')}
            helperText={getFieldError('filmId') || 'Format: ttXXXXXXX'}
            disabled={isEditMode || disabled}
            placeholder="e.g., tt0133093"
            InputProps={{
              startAdornment: <Info sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>

        {/* Projection Date */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Projection Date"
            type="date"
            value={formData.projectionDate}
            onChange={(e) => onFieldChange('projectionDate', e.target.value)}
            error={hasFieldError('projectionDate')}
            helperText={getFieldError('projectionDate') || 'Date when projection was made'}
            disabled={disabled}
            InputProps={{
              startAdornment: <CalendarToday sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />
            }}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              min: '1900-01-01',
              max: '2100-12-31'
            }}
          />
        </Grid>

        {/* Actual Projection */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Actual Projection"
            value={formData.actualProjection}
            onChange={(e) => handleCurrencyChange('actualProjection', e.target.value)}
            error={hasFieldError('actualProjection')}
            helperText={getFieldError('actualProjection') || 'Most likely opening weekend box office'}
            disabled={disabled}
            placeholder="50000000"
            InputProps={{
              startAdornment: <AttachMoney sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />,
              endAdornment: formData.actualProjection && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatCurrencyDisplay(formData.actualProjection)}
                </Typography>
              )
            }}
          />
        </Grid>

        {/* Outlier Minimum */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Outlier Minimum"
            value={formData.outlierMinimum}
            onChange={(e) => handleCurrencyChange('outlierMinimum', e.target.value)}
            error={hasFieldError('outlierMinimum')}
            helperText={getFieldError('outlierMinimum') || 'Farthest floor expectation'}
            disabled={disabled}
            placeholder="30000000"
            InputProps={{
              startAdornment: <TrendingDown sx={{ fontSize: '1rem', mr: 1, color: 'error.main' }} />,
              endAdornment: formData.outlierMinimum && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatCurrencyDisplay(formData.outlierMinimum)}
                </Typography>
              )
            }}
          />
        </Grid>

        {/* Outlier Ceiling */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Outlier Ceiling"
            value={formData.outlierCeiling}
            onChange={(e) => handleCurrencyChange('outlierCeiling', e.target.value)}
            error={hasFieldError('outlierCeiling')}
            helperText={getFieldError('outlierCeiling') || 'Farthest ceiling expectation'}
            disabled={disabled}
            placeholder="80000000"
            InputProps={{
              startAdornment: <TrendingUp sx={{ fontSize: '1rem', mr: 1, color: 'success.main' }} />,
              endAdornment: formData.outlierCeiling && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatCurrencyDisplay(formData.outlierCeiling)}
                </Typography>
              )
            }}
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => onFieldChange('notes', e.target.value)}
            error={hasFieldError('notes')}
            helperText={getFieldError('notes') || 'Additional context or reasoning (max 500 characters)'}
            disabled={disabled}
            multiline
            rows={3}
            placeholder="Add any additional context or reasoning for this projection..."
            inputProps={{ maxLength: 500 }}
          />
        </Grid>
      </Grid>

      {/* Calculated Values Display */}
      {formData.actualProjection && (
        <Box sx={{ mt: 4, p: 3, backgroundColor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Calculated Values
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Projection Floor (90%)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {formatCurrencyDisplay(calculatedValues.projectionFloor)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Projection Ceiling (110%)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {formatCurrencyDisplay(calculatedValues.projectionCeiling)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Range
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrencyDisplay(calculatedValues.range)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Confidence Level
                </Typography>
                <Chip
                  label={confidence.level}
                  color={confidence.color}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Range Percentage Info */}
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'white', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Range Percentage:</strong> {calculatedValues.rangePercentage.toFixed(1)}%
              {calculatedValues.rangePercentage <= 50 && ' (High Confidence)'}
              {calculatedValues.rangePercentage > 50 && calculatedValues.rangePercentage <= 100 && ' (Medium Confidence)'}
              {calculatedValues.rangePercentage > 100 && ' (Low Confidence)'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Please fix the following validation errors:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {Object.entries(errors).map(([field, error]) => (
              <li key={field}>
                <Typography variant="body2">
                  <strong>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {error}
                </Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Help Information */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutline sx={{ fontSize: '1rem' }} />
          Projection Guidelines
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • <strong>Actual Projection:</strong> Your best estimate for opening weekend box office<br/>
          • <strong>Outlier Minimum:</strong> The lowest reasonable expectation<br/>
          • <strong>Outlier Ceiling:</strong> The highest reasonable expectation<br/>
          • <strong>Projection Floor/Ceiling:</strong> Automatically calculated as ±10% of actual projection<br/>
          • <strong>Confidence Level:</strong> Based on the range between outlier minimum and ceiling
        </Typography>
      </Box>
    </Box>
  );
};

export default ProjectionForm;
